"use client"; 

import { ErrorModal } from '@/components/ErrorModal';
import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

interface ErrorContextProps {
  showError: (error: string, isFatal?: boolean) => void;
  hideError: () => void;
}

const ErrorContext = createContext<ErrorContextProps | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

export const ErrorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFatal, setIsFatal] = useState<boolean>(false);

  const showError = useCallback((error: string, fatal: boolean = false) => {
    setErrorMessage(error);
    setIsFatal(fatal);
  }, []);

  const hideError = useCallback(() => {
    setErrorMessage(null);
    setIsFatal(false);
  }, []);

  const value = useMemo(() => ({ showError, hideError }), [showError, hideError]);

  return (
    <ErrorContext.Provider value={value}>
      {children}
      <ErrorModal isOpen={!!errorMessage} onClose={hideError} isFatal={isFatal} />
    </ErrorContext.Provider>
  );
};
